import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

// Interfaces
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// MUI
import { GridFilterModel } from "@mui/x-data-grid-premium";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API
import { getCITMaxValues } from "@DataPages/api/financeManagement/cit";

// Layouts
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";

// Components
import { MyCitModel } from "./MyCitModel";
import { RootState } from "@/redux/store";
import { MyCitContextProvider } from "@/DataPages/context/myCit/MyCitContext";
import { getMyCit } from "@/DataPages/api/myCIT";

const QUERY_IDENTIFIERS = ["myCashInTransit", "CITGraphMaxValues", "allFinanceManagers"];

const isCITUpdatable = (periodSelected: IPeriodTreeSelection) =>
	Boolean(periodSelected?.cycleStatus === "CLOSING" || periodSelected?.cycleStatus === "ACTIVE");

export default function MyCitLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<IPeriodTreeSelection>(null);
	const open = useRef(true);
	const [selected, setSelected] = useState<IPeriodTreeSelection>(null);

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const locationId = useSelector((state: RootState) => state.location.location?.locationId ?? "");
	const employeeId = useSelector((state: RootState) => state.user.user?.employeeId ?? "");

	const sideBar = useMemo(() => {
		const sideSelection = function (newSelection: IPeriodTreeSelection) {
			if (newSelection?._id !== lastSelected.current?._id) {
				lastSelected.current = newSelection;
				setSelected(newSelection);
			}
		};

		return <PayPeriodSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription]);

	useEffect(() => {
		return () => {
			lastSelected.current = null;
			open.current = true;
			setSelected(null);
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [locationId, clearSubscriptionContext]);

	const { data, isLoading: citLoading } = useQuery({
		queryKey: ["myCashInTransit", locationId, selected?.payPeriodId],
		queryFn: async () => {
			const result = await getMyCit(locationId, selected?.cycleId ?? "", selected?.payPeriodId ?? "", employeeId);
			if (!result) {
				throw new Error("Error getting myCIT");
			}
			return result;
		},
		enabled: !!locationId && !!selected?.payPeriodId,
		staleTime: Infinity,
	});

	const { data: citMaxValues, isLoading: CitGraphMaxValuesLoading } = useQuery({
		queryKey: ["CITGraphMaxValues", locationId],
		queryFn: async () => {
			const result = await getCITMaxValues(locationId);
			if (!result) {
				throw new Error("Error getting CIT max values");
			}
			return result;
		},
		staleTime: Infinity,
	});

	const [filterModel, setFilterModel] = useState<GridFilterModel>({
		items: [],
	});

	return (
		<BodyLayer floatingContent={<CollapsibleLeftPanel permanentTrigger={open.current} control={sideBar} />}>
			<MyCitContextProvider
				citSettings={citMaxValues}
				deals={data?.deals ?? []}
				myCit={data}
				filterModel={filterModel}
				setFilterModel={setFilterModel}
			>
				<MyCitModel
					selected={selected}
					dataPresent={Boolean(selected)}
					isUpdatable={isCITUpdatable(selected)}
					isLoading={citLoading || CitGraphMaxValuesLoading}
				/>
			</MyCitContextProvider>
		</BodyLayer>
	);
}
